<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import AssistCardsAPI from 'dashboard/api/assistCards';
import ChipPopover from 'dashboard/components-next/Conversation/ContextChips/ChipPopover.vue';
import AssistCardItems from './AssistCardItems.vue';

// SiteUp (Cartões de Apoio): o chip que já veio com dado abre um painel em vez de mandar
// o atendente pra outra tela. O dado chega pronto no envelope da sugestão (pré-geração);
// aqui só existe a recarga sob demanda, que é assíncrona no backend: o POST devolve
// `processing` até o job gravar, então perguntamos de novo em intervalo.
//
// Não há estado vazio aqui, de propósito: o AssistChips só entrega um cartão a este painel
// quando ele TEM item (cartão de pedidos/acessos sem item é descartado, e o de imóveis sem
// item vira o botão que abre o modal, que busca na hora e pagina). Recarga que não acha
// nada mantém o dado anterior na tela e acende a linha de falha.
const props = defineProps({
  card: {
    type: Object,
    required: true,
  },
  conversationId: {
    type: [Number, String],
    default: null,
  },
  // Sem editor de resposta na tela não existe "Ver todos": o modal insere no editor.
  allowPropertyModal: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['propertyModal']);

const { t } = useI18n();

const POLL_INTERVAL = 1500;
const MAX_POLLS = 8;

const open = ref(false);
const refreshed = ref(null);
const loading = ref(false);
const failed = ref(false);

let timer = null;
let alive = true;

// O dado recarregado vence o do envelope; sem recarga, o que veio na sugestão manda.
const items = computed(() => refreshed.value ?? props.card.items ?? []);
const showSeeAll = computed(
  () => props.allowPropertyModal && Boolean(props.card.source)
);

const clearTimer = () => {
  if (timer) clearTimeout(timer);
  timer = null;
};

const fail = () => {
  loading.value = false;
  failed.value = true;
};

const poll = async attempt => {
  if (!alive) return;
  const { data } = await AssistCardsAPI.refresh(props.conversationId);
  const fresh = (data?.cards || []).find(card => card.id === props.card.id);
  if (fresh?.items?.length) {
    refreshed.value = fresh.items;
    loading.value = false;
    return;
  }
  // Resposta com cartões e sem o nosso: o job terminou e não achou nada. Perguntar de
  // novo só gastaria request — o vazio é a resposta.
  if (data?.cards || attempt >= MAX_POLLS) {
    fail();
    return;
  }
  timer = setTimeout(() => poll(attempt + 1).catch(fail), POLL_INTERVAL);
};

const refresh = () => {
  if (loading.value || !props.conversationId) return;
  clearTimer();
  loading.value = true;
  failed.value = false;
  poll(1).catch(fail);
};

const toggle = () => {
  open.value = !open.value;
};

const seeAll = () => {
  open.value = false;
  emit('propertyModal', props.card.source);
};

onBeforeUnmount(() => {
  alive = false;
  clearTimer();
});
</script>

<template>
  <ChipPopover :open="open" @close="open = false">
    <template #trigger>
      <button
        v-tooltip.top="card.description"
        type="button"
        :class="card.class"
        @click="toggle"
      >
        <span
          v-if="loading"
          class="i-ri-loader-4-line size-3.5 shrink-0 animate-spin"
          aria-hidden="true"
        />
        <span
          v-else
          class="size-3.5 shrink-0"
          :class="card.icon"
          aria-hidden="true"
        />
        <span class="min-w-0 truncate">{{ card.label }}</span>
      </button>
    </template>

    <div class="flex flex-col gap-3">
      <span class="text-sm font-medium text-n-slate-12">{{ card.label }}</span>

      <AssistCardItems :kind="card.kind" :items="items" />

      <!-- A falha aparece mesmo com item na tela. Sem isto, o "Atualizar" de um cartão que
           já tinha dado podia falhar sem sinal nenhum, e o atendente leria o dado velho
           achando que acabara de atualizar (e passaria ao cliente um status vencido). -->
      <span v-if="failed" class="text-xs text-n-ruby-11">
        {{ t('CONVERSATION.ASSIST_CHIPS.ERROR') }}
      </span>

      <div class="flex items-center gap-2">
        <button
          type="button"
          class="rounded-lg border border-n-weak px-2 py-1 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
          :disabled="loading || !conversationId"
          @click="refresh"
        >
          {{
            loading
              ? t('CONVERSATION.ASSIST_CHIPS.LOADING')
              : t('CONVERSATION.ASSIST_CHIPS.REFRESH')
          }}
        </button>
        <button
          v-if="showSeeAll"
          type="button"
          class="rounded-lg bg-n-alpha-2 px-2 py-1 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-3"
          @click="seeAll"
        >
          {{ t('CONVERSATION.ASSIST_CHIPS.SEE_ALL') }}
        </button>
      </div>
    </div>
  </ChipPopover>
</template>
